import React, { type JSX } from 'react';
import { uniqueId } from 'lodash';
import { useStateWithCallback } from '../../../utils/useStateWithCallback';
import { FileList, FileListProps, UploadedFile, UploadedFileWithKey } from './FileList';

export interface UploaderWithListProps<T extends UploadedFile> {
  value?: T[];
  onChange: (files: T[]) => void;
  Uploader: ({ onUploadDone }: { onUploadDone: (uploadedFiles: T[]) => void }) => JSX.Element;
  listProps?: Omit<FileListProps<T>, 'files' | 'setFiles'>;
}

export const UploaderWithList = <T extends UploadedFile>({
  onChange,
  Uploader,
  listProps = {},
  value = [],
}: UploaderWithListProps<T>): JSX.Element => {
  const [files, setFiles] = useStateWithCallback<UploadedFileWithKey<T>[]>(
    value.map(x => ({ key: uniqueId(), file: x })),
    files => {
      onChange(files.map(x => x.file));
    },
  );

  const onUploadDone = (filesUploaded: T[]) => {
    setFiles(currentFiles => [...currentFiles, ...filesUploaded.map(x => ({ key: uniqueId(), file: x }))]);
  };

  return (
    <>
      <Uploader onUploadDone={onUploadDone} />
      <FileList files={files} setFiles={setFiles} {...listProps} />
    </>
  );
};
